import {ITimelineEntry} from 'components/ui/PaymentTimeline/PaymentTimeline';
import {PATH} from 'constants/common';
import {getIdFromIri} from 'helpers/common';
import {formatDateInZurichTZ, formatDateTime} from 'helpers/dates';
import {translate} from 'helpers/localize';
import {PayoutType} from 'types/payments';
import {
    Transaction,
    IPayoutTransactionDetail,
    IPayoutTransaction,
    IFxTransaction,
    IFxTransactionDetail,
    IPayinTransaction,
    IPayinTransactionDetail,
    IFeeTransaction,
    IWalletTransactionDetail,
    IWalletTransaction,
    ICardTransactionDetail,
    ICardTransaction,
    TransactionDetails,
    ICardTransactionDetailsPublic,
    ExpensableTransactionDetail,
} from 'types/transactions';

export const isPayoutTransaction = (
    transaction?: Transaction | TransactionDetails,
): transaction is IPayoutTransaction => {
    return transaction?.['@type'] === 'PayoutTransaction';
};

export const isPayoutTransactionDetail = (
    transaction?: Transaction | TransactionDetails,
): transaction is IPayoutTransactionDetail => {
    return transaction?.['@type'] === 'PayoutTransactionDetail';
};

export const isFxTransaction = (
    transaction: Transaction | TransactionDetails,
): transaction is IFxTransaction => {
    return transaction['@type'] === 'FxTransaction';
};

export const isFxTransactionDetail = (
    transaction: Transaction | TransactionDetails,
): transaction is IFxTransactionDetail => {
    return transaction['@type'] === 'FxTransactionDetail';
};

export const isPayinTransaction = (
    transaction: Transaction | TransactionDetails,
): transaction is IPayinTransaction => {
    return transaction['@type'] === 'PayinTransaction';
};

export const isPayinTransactionDetail = (
    transaction: Transaction | TransactionDetails,
): transaction is IPayinTransactionDetail => {
    return transaction['@type'] === 'PayinTransactionDetail';
};

export const isFeeTransactionDetail = (
    transaction: Transaction | TransactionDetails,
): transaction is IFeeTransaction => {
    return transaction['@type'] === 'FeeTransaction';
};

export const isWalletTransaction = (
    transaction: Transaction | TransactionDetails,
): transaction is IWalletTransaction => {
    return transaction['@type'] === 'WalletTransaction';
};

export const isCardTransaction = (
    transaction: Transaction | TransactionDetails | undefined,
): transaction is ICardTransaction => {
    return transaction?.['@type'] === 'CardTransaction';
};

export const isCardTransactionDetail = (
    transaction: Transaction | TransactionDetails | undefined,
): transaction is ICardTransactionDetail => {
    return (
        transaction?.['@type'] === 'CardTransactionDetail' &&
        (transaction as ICardTransactionDetailsPublic)?.expenseCategories ===
            undefined
    );
};

export const isExpensableTransactionOrDetail = (
    transaction?: Transaction | TransactionDetails,
): transaction is ExpensableTransactionDetail => {
    return (
        !!transaction &&
        (isTransaction(transaction) || isCardTransactionDetail(transaction))
    );
};

export const isTransaction = (
    transaction?: Transaction | TransactionDetails,
): transaction is Transaction => {
    return (
        !!transaction &&
        (transaction['@type'] === 'CardTransaction' ||
            transaction['@type'] === 'FeeTransaction' ||
            transaction['@type'] === 'FxTransaction' ||
            transaction['@type'] === 'PayinTransaction' ||
            transaction['@type'] === 'PayoutTransaction' ||
            transaction['@type'] === 'WalletTransaction')
    );
};

export const isTransactionDetail = (
    transaction: Transaction | TransactionDetails,
): transaction is TransactionDetails => {
    return !isTransaction(transaction);
};

export const createTimelineEntriesFromPaymentDetails = (
    payoutTransactionDetails: IPayoutTransactionDetail,
): ITimelineEntry[] => {
    const result: ITimelineEntry[] = [];

    result.push({
        comment: translate('Created'),
        status: 'done',
        label: formatDateTime(payoutTransactionDetails.createdAt || new Date()),
    });
    result.push(
        ...payoutTransactionDetails.signoffs
            .reverse()
            .reduce<ITimelineEntry[]>((acc, curr) => {
                acc.push({
                    status: 'done',
                    comment: translate('Signoff by: %{firstName} %{lastName}', {
                        firstName: curr.user.firstName,
                        lastName: curr.user.lastName,
                    }),
                    label: formatDateTime(curr.createdAt),
                });

                return acc;
            }, []),
    );

    if (payoutTransactionDetails.status === 'needs_signature') {
        result.push({
            label: translate('Waiting for signoff(s)'),
            comment: translate('Missing signature'),
            status: 'waiting',
        });
    }

    result.push(
        {
            label: (() => {
                if (payoutTransactionDetails.walletExecutedAt)
                    return formatDateTime(
                        payoutTransactionDetails.walletExecutedAt,
                    );

                if (
                    new Date(payoutTransactionDetails.executionDate).getTime() >
                    new Date().getTime()
                )
                    return formatDateInZurichTZ(
                        payoutTransactionDetails.executionDate,
                    );

                return formatDateInZurichTZ(new Date());
            })(),
            comment: translate('Execution'),
            status:
                payoutTransactionDetails.status !== 'executed'
                    ? 'pending'
                    : 'done',
        },
        {
            label: payoutTransactionDetails.transferredAt
                ? formatDateTime(payoutTransactionDetails.transferredAt)
                : '-',
            comment: translate(
                !payoutTransactionDetails.transferredAt ||
                    new Date(payoutTransactionDetails.transferredAt).getTime() >
                        new Date().getTime()
                    ? 'Transferring to the bank'
                    : 'Transferred to the bank',
            ),
            status:
                !payoutTransactionDetails.transferredAt ||
                new Date(payoutTransactionDetails.transferredAt).getTime() >
                    new Date().getTime()
                    ? 'pending'
                    : 'done',
        },
        {
            label: (() => {
                if (!payoutTransactionDetails.arrivalAt) return '-';
                if (!payoutTransactionDetails.transferredAt)
                    return translate('%{date} end of day', {
                        date: formatDateInZurichTZ(
                            payoutTransactionDetails.arrivalAt,
                        ),
                    });
                if (!payoutTransactionDetails.bankCurrencyCloud) {
                    return formatDateInZurichTZ(
                        payoutTransactionDetails.arrivalAt,
                    );
                }
                return formatDateTime(payoutTransactionDetails.arrivalAt);
            })(),
            comment: translate(
                payoutTransactionDetails.status !== 'executed' ||
                    new Date(payoutTransactionDetails.arrivalAt).getTime() >
                        new Date().getTime()
                    ? 'Expected time of arrival'
                    : 'Arrived',
            ),
            status:
                payoutTransactionDetails.status !== 'executed' ||
                new Date(payoutTransactionDetails.arrivalAt).getTime() >
                    new Date().getTime()
                    ? 'pending'
                    : 'done',
        },
    );

    return result;
};

export const createTimelineEntriesFromPeerDetails = (
    details: IWalletTransactionDetail & {
        executed: boolean;
        executionDate: string;
    },
): ITimelineEntry[] => {
    const result: ITimelineEntry[] = [];
    result.push({
        comment: translate('Created'),
        status: 'done',
        label: formatDateTime(details.createdAt),
    });
    result.push(
        ...details.signoffs.reverse().reduce<ITimelineEntry[]>((acc, curr) => {
            acc.push({
                status: 'done',
                comment: translate('Signoff by: %{firstName} %{lastName}', {
                    firstName: curr.user.firstName,
                    lastName: curr.user.lastName,
                }),
                label: formatDateTime(curr.createdAt),
            });

            return acc;
        }, []),
    );

    if (details.status === 'needs_signature') {
        result.push({
            comment: translate('Missing signature'),
            label: translate('Waiting for signoff(s)'),
            status: 'waiting',
        });
    }

    result.push(
        {
            label: formatDateTime(details.executionDate),
            comment: translate('Execution'),
            status: details.executed ? 'done' : 'pending',
        },
        {
            label: formatDateTime(details.executionDate),
            comment: translate(
                details.executed ? 'Arrived' : 'Expected time of arrival',
            ),
            status: details.executed ? 'done' : 'pending',
        },
    );
    return result;
};

export const createEditPayoutLocation = (
    data: IPayoutTransaction | IPayoutTransactionDetail,
): {pathname: string; search: string} => {
    return {
        pathname: PATH.PAYOUT,
        search: new URLSearchParams({
            payoutId: data['@id'],
            paymentType: PayoutType.TRADITIONAL,
            contactId: data.contact?.['@id'].split('/').pop() || '',
            step: '1',
        }).toString(),
    };
};

export const getTransactionIdFromTransactionOrDetail = (
    transactionOrDetail:
        | ICardTransactionDetail
        | ICardTransactionDetailsPublic
        | Transaction,
) => {
    const iri = isCardTransactionDetail(transactionOrDetail)
        ? transactionOrDetail.transaction?.['@id'] || transactionOrDetail['@id']
        : transactionOrDetail['@id'];

    return getIdFromIri(iri);
};
